import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { subscriptionAPI, paymentsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Check, CreditCard, Zap, Crown, Star, Calendar, Loader2, Wallet } from 'lucide-react';

const PLAN_ICONS = { free_trial: Zap, pro: CreditCard, enterprise: Crown };
const PLAN_COLORS = { free_trial: 'text-green-500', pro: 'text-blue-500', enterprise: 'text-purple-500' };

const DURATION_LABELS = {
  monthly: 'Mensile',
  annual: 'Annuale',
  biennial: 'Biennale'
};

const DURATION_ORDER = ['monthly', 'annual', 'biennial'];

export default function SubscriptionPage() {
  const { user, checkAuth } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDurations, setSelectedDurations] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [currentPlan, setCurrentPlan] = useState('free_trial');
  const [plans, setPlans] = useState([]);
  const [trialDays, setTrialDays] = useState(14);
  const [subInfo, setSubInfo] = useState(null);

  useEffect(() => { 
    loadConfig(); 
    // Refresh user data on page load to get latest subscription info
    checkAuth();
  }, []);
  useEffect(() => { setCurrentPlan(user?.subscription_plan || 'free_trial'); }, [user]);
  
  // Handle Stripe return
  useEffect(() => {
    const success = searchParams.get('success');
    const sessionId = searchParams.get('session_id');
    const canceled = searchParams.get('canceled');
    
    if (success === 'true' && sessionId) {
      handlePaymentSuccess(sessionId);
      setSearchParams({});
    } else if (canceled === 'true') {
      toast.error('Pagamento annullato');
      setSearchParams({});
    }
  }, [searchParams]);

  const handlePaymentSuccess = async (sessionId) => {
    setLoading(true);
    try {
      for (let i = 0; i < 10; i++) {
        const res = await paymentsAPI.getSessionStatus(sessionId);
        if (res.data.status === 'completed') {
          toast.success('Pagamento completato! Il tuo piano è stato aggiornato.');
          await checkAuth();
          await loadConfig();
          break;
        }
        await new Promise(r => setTimeout(r, 1000));
      }
    } catch {
      toast.error('Errore nella verifica del pagamento');
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      const [configRes, subRes] = await Promise.all([
        subscriptionAPI.getPublicConfig(),
        subscriptionAPI.get()
      ]);
      const loadedPlans = configRes.data.plans || [];
      setPlans(loadedPlans);
      setTrialDays(configRes.data.trial_days || 14);
      setSubInfo(subRes.data);
      
      // Initialize selected durations for each plan
      const defaultDurations = {};
      loadedPlans.forEach(plan => {
        const availableDurations = getAvailableDurations(plan);
        if (availableDurations.length > 0) {
          // Prefer annual, then the first available
          defaultDurations[plan.id] = availableDurations.includes('annual') 
            ? 'annual' 
            : availableDurations[0];
        }
      });
      setSelectedDurations(defaultDurations);
    } catch {
      const fallbackPlans = [
        { id: 'free_trial', name: 'Prova Gratuita', max_links: 3, features: ['14 giorni di prova', 'OBC limitati', 'Supporto base'], available_for: ['user', 'integrator'], durations: { annual: { enabled: true, price: 0 } } },
        { id: 'pro', name: 'Pro', max_links: 50, features: ['OBC fino a 50', 'Supporto prioritario'], available_for: ['user', 'integrator'], durations: { monthly: { enabled: true, price: 9.99 }, annual: { enabled: true, price: 99 }, biennial: { enabled: true, price: 179 } } },
        { id: 'enterprise', name: 'Enterprise', max_links: null, features: ['OBC illimitati', 'Supporto dedicato'], available_for: ['user', 'integrator'], durations: { monthly: { enabled: true, price: 29.99 }, annual: { enabled: true, price: 299 }, biennial: { enabled: true, price: 499 } } }
      ];
      setPlans(fallbackPlans);
      setSelectedDurations({ pro: 'annual', enterprise: 'annual' });
    } finally {
      setConfigLoading(false);
    }
  };

  // Check if plan is available for current user role
  const isPlanAvailableForUser = (plan) => {
    const userRole = user?.role || 'user';
    // Root and admin can see all plans
    if (['root', 'administrator'].includes(userRole)) return true;
    const availableFor = plan.available_for || ['user', 'integrator'];
    return availableFor.includes(userRole);
  };

  // Get available durations for a plan
  const getAvailableDurations = (plan) => {
    if (plan.durations) {
      return DURATION_ORDER.filter(d => plan.durations[d]?.enabled);
    }
    // Old format fallback
    const available = [];
    if (plan.price_annual !== undefined) available.push('annual');
    if (plan.price_biannual !== undefined || plan.price_biennial !== undefined) available.push('biennial');
    return available;
  };

  // Get price for a specific duration
  const getPriceForDuration = (plan, durationId) => {
    if (plan.durations && plan.durations[durationId]) {
      return plan.durations[durationId].price || 0;
    }
    // Old format fallback
    if (durationId === 'annual') return plan.price_annual || 0;
    if (durationId === 'biennial' || durationId === 'biannual') return plan.price_biannual || plan.price_biennial || 0;
    return 0;
  };

  const handleUpgrade = async (planId) => {
    if (planId === 'free_trial') return;
    
    const durationId = selectedDurations[planId] || 'annual';
    
    setLoadingPlan(planId);
    try {
      const fullPlanId = `${planId}_${durationId}`;
      const originUrl = window.location.origin;
      
      const res = await paymentsAPI.createSubscriptionCheckout(fullPlanId, originUrl);
      window.location.href = res.data.checkout_url;
    } catch (err) {
      const detail = err.response?.data?.detail || "Errore nella creazione del checkout";
      if (detail.includes('Stripe non configurato')) {
        toast.error('Stripe non configurato. Contatta l\'amministratore.');
      } else {
        toast.error(detail);
      }
      setLoadingPlan(null);
    }
  };

  const trialEnd = user?.trial_end ? new Date(user.trial_end) : null;
  const daysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - new Date()) / (1000 * 60 * 60 * 24))) : 0;

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  const getDurationSuffix = (durationId) => {
    const suffixes = {
      monthly: '/mese',
      annual: '/anno',
      biennial: '/2 anni'
    };
    return suffixes[durationId] || '';
  };

  if (configLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }} data-testid="subscription-title">
          Abbonamento
        </h1>
        <p className="text-muted-foreground mt-1">Gestisci il tuo piano di abbonamento</p>
      </div>

      {/* Current Subscription Info */}
      {subInfo && subInfo.subscription_start && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <Calendar className="w-5 h-5 text-primary" />
              <span className="font-semibold text-sm" style={{ fontFamily: 'Manrope' }}>Abbonamento Attivo</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Inizio</p>
                <p className="font-medium" data-testid="sub-start-date">{formatDate(subInfo.subscription_start)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Scadenza</p>
                <p className="font-medium" data-testid="sub-end-date">{formatDate(subInfo.subscription_end)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Periodo</p>
                <p className="font-medium" data-testid="sub-billing-period">
                  {DURATION_LABELS[subInfo.billing_period] || subInfo.billing_period}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trial banner - show only if trial has expiration */}
      {user?.subscription_status === 'trial' && user?.trial_end && (
        <Card className="border-yellow-500/20 bg-yellow-500/5">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                <Zap className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <p className="font-medium text-sm">Periodo di Prova ({trialDays} giorni)</p>
                <p className="text-xs text-muted-foreground">
                  {daysLeft > 0 ? `${daysLeft} giorni rimanenti` : 'Periodo di prova scaduto'}
                </p>
              </div>
            </div>
            <Badge className="bg-yellow-500/15 text-yellow-500 border-yellow-500/20 border" data-testid="trial-badge">
              Trial
            </Badge>
          </CardContent>
        </Card>
      )}

      {/* Free Plan info - no expiration */}
      {user?.subscription_plan === 'free_trial' && !user?.trial_end && (
        <Card className="border-green-500/20 bg-green-500/5">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <Zap className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="font-medium text-sm">Piano Gratuito</p>
                <p className="text-xs text-muted-foreground">
                  Passa a un piano a pagamento per sbloccare tutte le funzionalità
                </p>
              </div>
            </div>
            <Badge className="bg-green-500/15 text-green-500 border-green-500/20 border" data-testid="free-badge">
              Free
            </Badge>
          </CardContent>
        </Card>
      )}

      {/* Payment Methods Info */}
      <div className="flex items-center justify-center gap-4 py-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Wallet className="w-4 h-4" />
          <span>Pagamenti sicuri con:</span>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-xs">Carta</Badge>
          <Badge variant="outline" className="text-xs">Google Pay</Badge>
          <Badge variant="outline" className="text-xs">Apple Pay</Badge>
        </div>
      </div>

      {/* Plans */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {plans.filter(isPlanAvailableForUser).map((plan, idx) => {
          const IconComp = PLAN_ICONS[plan.id] || Star;
          const color = PLAN_COLORS[plan.id] || 'text-blue-500';
          const filteredPlans = plans.filter(isPlanAvailableForUser);
          const isPopular = filteredPlans.length > 1 && idx === 1;
          const availableDurations = getAvailableDurations(plan);
          const selectedDuration = selectedDurations[plan.id] || availableDurations[0] || 'annual';
          const price = getPriceForDuration(plan, selectedDuration);

          return (
            <Card
              key={plan.id}
              data-testid={`plan-card-${plan.id}`}
              className={`border-border bg-card relative transition-all duration-200 ${
                isPopular ? 'border-primary/50 shadow-lg shadow-primary/5' : ''
              } ${currentPlan === plan.id ? 'ring-2 ring-primary' : ''}`}
            >
              {isPopular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground">Popolare</Badge>
                </div>
              )}
              <CardHeader className="text-center pb-2">
                <div className={`w-12 h-12 mx-auto rounded-lg ${color.replace('text-', 'bg-').replace('500', '500/10')} flex items-center justify-center mb-3`}>
                  <IconComp className={`w-6 h-6 ${color}`} />
                </div>
                <CardTitle style={{ fontFamily: 'Manrope' }}>{plan.name}</CardTitle>
                
                {/* Duration Selector */}
                {availableDurations.length > 1 && plan.id !== 'free_trial' && (
                  <div className="mt-3">
                    <Select
                      value={selectedDuration}
                      onValueChange={(val) => setSelectedDurations(prev => ({ ...prev, [plan.id]: val }))}
                    >
                      <SelectTrigger className="w-full h-9 text-sm" data-testid={`duration-select-${plan.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {availableDurations.map(d => (
                          <SelectItem key={d} value={d}>
                            {DURATION_LABELS[d]} - €{getPriceForDuration(plan, d)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="mt-3">
                  <span className="text-3xl font-bold">€{price}</span>
                  <span className="text-muted-foreground text-sm">
                    {price === 0 ? '' : getDurationSuffix(selectedDuration)}
                  </span>
                </div>
                <div className="flex gap-2 justify-center mt-1">
                  {plan.max_links !== null && (
                    <span className="text-xs text-muted-foreground">Max {plan.max_links} OBC</span>
                  )}
                  {plan.max_links === null && (
                    <span className="text-xs text-muted-foreground">OBC illimitati</span>
                  )}
                  {plan.max_tickets !== null && plan.max_tickets !== undefined && (
                    <span className="text-xs text-muted-foreground">| {plan.max_tickets === 0 ? 'No ticket' : `${plan.max_tickets} ticket/anno`}</span>
                  )}
                  {(plan.max_tickets === null || plan.max_tickets === undefined) && plan.id === 'enterprise' && (
                    <span className="text-xs text-muted-foreground">| Ticket illimitati</span>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {(plan.features || []).map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <Check className={`w-4 h-4 ${color} flex-shrink-0`} />
                      <span className="text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  data-testid={`select-plan-${plan.id}`}
                  className={`w-full ${
                    currentPlan === plan.id
                      ? 'bg-secondary text-secondary-foreground'
                      : isPopular
                        ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                        : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                  }`}
                  disabled={currentPlan === plan.id || loadingPlan === plan.id || plan.id === 'free_trial'}
                  onClick={() => handleUpgrade(plan.id)}
                >
                  {loadingPlan === plan.id ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Caricamento...
                    </>
                  ) : currentPlan === plan.id ? (
                    'Piano Corrente'
                  ) : plan.id === 'free_trial' ? (
                    'Solo Nuovi Utenti'
                  ) : (
                    'Acquista'
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {loading && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 flex items-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
            <span>Verifica pagamento in corso...</span>
          </div>
        </div>
      )}

      <p className="text-center text-xs text-muted-foreground">
        I pagamenti sono gestiti tramite Stripe. Supportiamo carte di credito/debito, Google Pay e Apple Pay.
      </p>
    </div>
  );
}
